import { eq } from 'drizzle-orm'
import { OpenImgContextProvider } from 'openimg/react'
import { useEffect } from 'react'
import {
	data,
	href,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ComingSoon } from '#app/components/coming-soon.tsx'
import { db } from '#app/db/index.ts'
import {
	rolesToUsers,
	users,
	waitlistSubscribers,
} from '#app/db/schema/index.ts'
import { isComingSoonMode } from '#app/utils/site-settings.server.ts'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { CookieConsentBanner } from './components/cookie-consent-banner.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { JsonLd } from './components/json-ld.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useAnalyticsIdentify } from './hooks/use-analytics-identify.ts'
import { useAnalyticsPageview } from './hooks/use-analytics-pageview.ts'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { cachified, lruCache } from './utils/cache.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import {
	checkImpersonationExpiry,
	getImpersonationMetadata,
} from './utils/impersonation.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { buildMeta, ORGANIZATION_LD } from './utils/seo.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time, type Timings } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data, matches }) => {
	// This is the default/base tag set. Individual routes override `meta` with
	// their own `buildMeta(...)` call so canonical + OG + Twitter stay in sync
	// (a child route's meta fully replaces the parent's in React Router).
	if (!data) return [{ title: 'Error | Syrcha' }]

	return buildMeta({
		title: 'Syrcha - Privacy Compliance Scanner for GDPR, CCPA & AI Act',
		description:
			'Scan your website for GDPR, CCPA, and AI Act compliance issues in minutes. Automated privacy monitoring with actionable recommendations.',
		matches,
	})
}

/**
 * Fetch the roles→permissions graph for a user, cached per user for 60s in
 * the in-memory LRU (TTL-only invalidation - role/permission changes take up
 * to 60s to propagate to active sessions). The graph rarely changes, so this
 * avoids re-joining roles and permissions on every request.
 */
function getUserRoles(userId: string, timings?: Timings) {
	return cachified(
		{
			key: `user-roles:${userId}`,
			cache: lruCache,
			ttl: 1000 * 60,
			timings,
			async getFreshValue() {
				const roleRows = await db.query.rolesToUsers.findMany({
					where: eq(rolesToUsers.B, userId),
					with: {
						role: {
							columns: { name: true },
							with: {
								permissionsToRoles: {
									with: {
										permission: {
											columns: {
												entity: true,
												action: true,
												access: true,
											},
										},
									},
								},
							},
						},
					},
				})
				return roleRows.map((rtu) => ({
					name: rtu.role.name,
					permissions: rtu.role.permissionsToRoles.map((ptr) => ({
						entity: ptr.permission.entity,
						action: ptr.permission.action,
						access: ptr.permission.access,
					})),
				}))
			},
		},
		// Silence the default verbose reporter - this runs on every request.
		() => () => {},
	)
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')

	// Wrap database operations in try-catch to handle unavailable database gracefully
	const { user, comingSoon } = await (async () => {
		try {
			const userId = await time(() => getUserId(request), {
				timings,
				type: 'getUserId',
				desc: 'getUserId in root',
			})

			const user = userId
				? await time(
						async () => {
							const [userRow, roles] = await Promise.all([
								db.query.users.findFirst({
									where: eq(users.id, userId),
									columns: { id: true, name: true, email: true, image: true },
									with: { userImage: { columns: { objectKey: true } } },
								}),
								getUserRoles(userId, timings),
							])
							return userRow ? { ...userRow, roles } : null
						},
						{ timings, type: 'find user', desc: 'find user in root' },
					)
				: null

			if (userId && !user) {
				console.info(
					'User authenticated but not found in database - logging out',
				)
				// something weird happened... The user is authenticated but we can't find
				// them in the database. Maybe they were deleted? Let's log them out.
				await logout({ request, redirectTo: href('/') })
			}

			const comingSoon = await isComingSoonMode()
			return { userId, user, comingSoon }
		} catch (error) {
			// Log the error but continue - app can still render without user data
			console.error('Error loading user in root loader:', error)
			return { userId: null, user: null, comingSoon: false }
		}
	})()

	// Check for impersonation expiry (auto-ends if expired)
	const expiryHeaders = await checkImpersonationExpiry(request)

	// Get impersonation metadata
	const impersonationMetadata = await getImpersonationMetadata(request)

	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			comingSoon,
			impersonation: impersonationMetadata,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				expiryHeaders ?? {},
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

const waitlistEmailSchema = z.object({
	email: z.string().email('Please enter a valid email address.'),
})

export async function action({ request }: Route.ActionArgs) {
	let formData: FormData
	try {
		formData = await request.formData()
	} catch {
		// ponytail: bots POST malformed multipart bodies to /; reject without reporting
		return data(
			{ status: 'error' as const, error: 'Invalid request body.' },
			{ status: 400 },
		)
	}
	const result = waitlistEmailSchema.safeParse({ email: formData.get('email') })

	if (!result.success) {
		return data(
			{
				status: 'error' as const,
				error: result.error.issues[0]?.message ?? 'Invalid email.',
			},
			{ status: 400 },
		)
	}

	await db
		.insert(waitlistSubscribers)
		.values({ email: result.data.email })
		.onConflictDoNothing({ target: waitlistSubscribers.email })

	return { status: 'success' as const }
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html
			lang="en"
			className={`${theme === 'dark' ? 'dark' : ''} h-full overflow-x-hidden`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{/* Organization / SoftwareApplication JSON-LD is emitted via route
				    meta (buildMeta in #app/utils/seo.ts). An inline <head> script is
				    duplicated by React 19 on hydration; React Router's <Meta> keys it. */}
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<HashScroll />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

/**
 * Scrolls to `#some-id` after hydration.
 *
 * Two things defeated the browser's own anchor jump: `<ScrollRestoration>`
 * sends a fresh load to the top when it has no saved position, and the marketing
 * page keeps growing after hydration as the scroll-reveal sections settle, so
 * the offset the browser resolved at parse time is stale by the time the page
 * is laid out. The result was that every `/#pricing`, `/#frameworks` and `/#how`
 * link - three of four header items and four footer links - landed at the hero.
 *
 * `key` is in the dependency list so clicking the same anchor twice scrolls
 * again; the hash string alone would not change. Scroll behaviour is left to
 * CSS, which already honours `prefers-reduced-motion`.
 */
function HashScroll() {
	const { hash, key } = useLocation()
	useEffect(() => {
		if (!hash) return
		const target = document.getElementById(hash.slice(1))
		if (!target) return
		const frame = requestAnimationFrame(() =>
			target.scrollIntoView({ block: 'start' }),
		)
		return () => cancelAnimationFrame(frame)
	}, [hash, key])
	return null
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)
	useAnalyticsPageview()
	useAnalyticsIdentify(data.user)

	if (data.comingSoon) {
		return <ComingSoon />
	}

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="min-h-screen">
				{/* Must be nested inside a real DOM element - NOT in <head> and not
				    as a direct child of <body>. React 19 handles document-level
				    <script> nodes as resources rather than hydrating them, so a
				    server-rendered copy in those positions is left orphaned and a
				    second one is inserted. Nested scripts hydrate normally - the
				    SoftwareApplication and FAQPage blocks render inside <main> and
				    have never duplicated. */}
				<JsonLd data={ORGANIZATION_LD} />
				<Outlet />
			</div>
			<CookieConsentBanner />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
